import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { REDO_EFFECT_API_URL } from "@redotech/redo-merchant-app-common/config";
import {
  EffectApiClientContext,
  EffectApiClientProvider,
  type EffectApiContext,
} from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { useSupportHelpdeskV2Enabled } from "@redotech/redo-merchant-app-common/setting/use-support-helpdesk-v2-enabled";
import { reactQueryClient } from "@redotech/redo-web/rpc-query/react-query-config";
import {
  createContext,
  memo,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import { createGlobalSupportTicketCollections } from "../db/collections";
import { useEncodedSupportTicketFilterGroup } from "../filters/filters-context";
import { NotificationSyncHandlers } from "../stream/notification-sync-handlers";
import { TeamNotificationStreamProvider } from "../stream/team-notification-stream-provider";
import { GlobalSupportTicketCollectionsContext } from "./global-support-ticket-collections-context";

const GlobalSupportTicketStoreProvider = memo(
  function GlobalSupportTicketStoreProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const effectApiClient = useRequiredContext(EffectApiClientContext);
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const merchantClient = useRequiredContext(RedoMerchantClientContext);
    const encodedFilterGroup = useEncodedSupportTicketFilterGroup();

    // Keep a ref to the latest effectApi so the stable proxy always calls
    // through to the current (non-disposed) runtime.
    const effectApiRef = useRef(effectApiClient);
    effectApiRef.current = effectApiClient;
    const encodedFilterGroupRef = useRef(encodedFilterGroup);
    encodedFilterGroupRef.current = encodedFilterGroup;

    const stableEffectApi = useMemo<EffectApiContext>(
      () => ({
        runPromise: (...args) => effectApiRef.current.runPromise(...args),
        getClient: () => effectApiRef.current.getClient(),
      }),
      // Stable proxy — intentionally created once, reads from ref at call time.

      [],
    );

    const collections = useMemo(() => {
      return createGlobalSupportTicketCollections({
        queryClient: reactQueryClient,
        effectApi: stableEffectApi,
        rpcClient,
        merchantClient,
        getEncodedFilterGroup: () => encodedFilterGroupRef.current,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stableEffectApi]);

    return (
      <GlobalSupportTicketCollectionsContext.Provider value={collections}>
        <NotificationSyncHandlers />
        {children}
      </GlobalSupportTicketCollectionsContext.Provider>
    );
  },
);

interface SupportProvidersProps {
  children: ReactNode;
}

const FeatureFlagEnabledContext = createContext<boolean | undefined>(undefined);
FeatureFlagEnabledContext.displayName = "FeatureFlagEnabledContext";

export function useFeatureFlagEnabled(): boolean {
  return useRequiredContext(FeatureFlagEnabledContext);
}

const FeatureFlagEnabledProvider = memo(function FeatureFlagEnabledProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { pending, enabled } = useSupportHelpdeskV2Enabled();

  if (pending) {
    return null;
  }

  return (
    <FeatureFlagEnabledContext.Provider value={enabled}>
      {children}
    </FeatureFlagEnabledContext.Provider>
  );
});

export const SupportProviders = memo(function SupportProviders({
  children,
}: SupportProvidersProps) {
  const auth = useContext(AuthContext);

  return (
    <EffectApiClientProvider
      authToken={auth?.token}
      baseUrl={REDO_EFFECT_API_URL}
    >
      <TeamNotificationStreamProvider
        topics={[
          "notifications",
          "presence",
          "ai-activity",
          "user-notifications",
        ]}
      >
        <GlobalSupportTicketStoreProvider>
          <FeatureFlagEnabledProvider>{children}</FeatureFlagEnabledProvider>
        </GlobalSupportTicketStoreProvider>
      </TeamNotificationStreamProvider>
    </EffectApiClientProvider>
  );
});
